import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { api, setToken, clearToken, getToken, formatApiError } from "@/lib/api";
import AdminTextsSection from "@/pages/AdminTextsSection";
import AdminBuilder from "@/pages/AdminBuilder";
import AdminPasswordSection from "@/pages/AdminPasswordSection";

const DEFAULT_CONFIG = {
  services: {
    noLabour: { base: 40, perKm: 1.4, includedKm: 25 },
    driverHelp: { base: 64, perKm: 1.6, includedKm: 25 },
    twoMen: { base: 99, perKm: 1.8, includedKm: 25 },
  },
  durations: { "30": 0, "60": 18, "90": 32, "120": 45, "180": 65 },
  blockedDates: [],
  phoneRaw: "+358401683383",
  phoneDisplay: "+358 40 168 3383",
  whatsappLink: "https://wa.me/358401683383",
  faqItems: [],
  whyItems: [],
  trustBadges: [],
  colors: null,
  heroImage: "",
};

const Notice = ({ children, kind = "info" }) => {
  const colors = {
    info: { bg: "#E5ECF5", color: "#1A4B8C", border: "#1A4B8C" },
    warn: { bg: "#FFE9D2", color: "#9A4A0A", border: "#F58220" },
    success: { bg: "#DDF6E5", color: "#1A6F3A", border: "#39C26F" },
  }[kind];
  return (
    <div
      className="px-4 py-3 rounded-xl text-sm"
      style={{
        background: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}30`,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
};

const Row = ({ label, sub, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start md:items-center py-3">
    <div>
      <div className="font-bold" style={{ color: "var(--navy)" }}>{label}</div>
      {sub && <div className="text-xs mt-0.5" style={{ color: "var(--mute)" }}>{sub}</div>}
    </div>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const PriceInput = ({ value, onChange, suffix = "€", width = 110 }) => (
  <div
    className="flex items-center rounded-xl border overflow-hidden"
    style={{ borderColor: "#DCE3ED", width, background: "#FFFFFF" }}
  >
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      step="0.1"
      min="0"
      className="w-full px-3 py-2 bg-transparent outline-none text-right font-bold"
      style={{ color: "var(--navy)" }}
    />
    <span
      style={{
        background: "var(--cream)",
        padding: "8px 10px",
        color: "var(--mute)",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {suffix}
    </span>
  </div>
);

const AddDurationForm = ({ onAdd, existing }) => {
  const [value, setValue] = useState("");
  const [err, setErr] = useState("");
  const submit = () => {
    setErr("");
    const m = parseInt(value, 10);
    if (!Number.isFinite(m) || m <= 0) {
      setErr("Entre un nombre de minutes valide (ex. 360 pour 6 h).");
      return;
    }
    const key = String(m);
    if (Object.prototype.hasOwnProperty.call(existing, key)) {
      setErr("Cette durée existe déjà.");
      return;
    }
    onAdd(m);
    setValue("");
  };
  return (
    <div
      className="mt-4 p-4 rounded-2xl"
      style={{ background: "var(--cream-2)" }}
      data-testid="add-duration-form"
    >
      <div className="text-xs font-bold mb-2" style={{ color: "var(--navy)" }}>
        ＋ AJOUTER UNE DURÉE (en minutes)
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex items-center gap-2 flex-1">
          <input
            type="number"
            min="1"
            step="1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="ex. 360"
            className="px-3 py-2 rounded-xl border outline-none text-[14px] font-bold"
            style={{ borderColor: "#DCE3ED", color: "var(--navy)", background: "#FFFFFF", width: 130 }}
            data-testid="duration-add-input"
          />
          <span className="text-sm" style={{ color: "var(--mute)" }}>
            minutes ={" "}
            {value && parseInt(value, 10) > 0
              ? (() => {
                  const m = parseInt(value, 10);
                  if (m < 60) return `${m} min`;
                  const h = Math.floor(m / 60);
                  const r = m % 60;
                  return r === 0 ? `${h} h` : `${h} h ${r}`;
                })()
              : "—"}
          </span>
        </div>
        <button
          type="button"
          onClick={submit}
          className="btn-primary"
          style={{ padding: "8px 18px", fontSize: 13 }}
          data-testid="duration-add-submit"
        >
          ＋ Ajouter
        </button>
      </div>
      {err && (
        <div className="text-xs mt-2" style={{ color: "#B3263F" }}>
          ⚠️ {err}
        </div>
      )}
      <div className="text-[11px] mt-2" style={{ color: "var(--mute)" }}>
        Astuce : pour 6 heures, tape <strong>360</strong>. Pour 9 heures, tape <strong>540</strong>.
      </div>
    </div>
  );
};

export default function AdminPanel() {
  const [authed, setAuthed] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [email, setEmail] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [savingMsg, setSavingMsg] = useState("");

  /* Restore session if previously authed */
  useEffect(() => {
    const t = getToken();
    if (!t) {
      setCheckingAuth(false);
      return;
    }
    api
      .me()
      .then(() => setAuthed(true))
      .catch(() => clearToken())
      .finally(() => setCheckingAuth(false));
  }, []);

  /* Fetch live config from backend when authed */
  useEffect(() => {
    if (!authed) return;
    setConfigLoading(true);
    api
      .getConfig()
      .then((c) => setConfig({ ...DEFAULT_CONFIG, ...c }))
      .finally(() => setConfigLoading(false));
  }, [authed]);

  const submitPw = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPwError("");
    try {
      const { token } = await api.login(email, pwInput);
      setToken(token);
      setAuthed(true);
    } catch (err) {
      setPwError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    clearToken();
    setAuthed(false);
    setEmail("");
    setPwInput("");
  };

  const updateService = (id, field, value) => {
    setConfig((c) => ({
      ...c,
      services: { ...c.services, [id]: { ...c.services[id], [field]: value } },
    }));
  };

  const updateDuration = (id, value) => {
    setConfig((c) => ({
      ...c,
      durations: { ...c.durations, [id]: value },
    }));
  };

  const addDuration = (minutes) => {
    const key = String(parseInt(minutes, 10));
    if (!key || key === "NaN" || key === "0") return false;
    if (config.durations && Object.prototype.hasOwnProperty.call(config.durations, key)) {
      return false;
    }
    setConfig((c) => ({
      ...c,
      durations: { ...(c.durations || {}), [key]: 0 },
    }));
    return true;
  };

  const removeDuration = (id) => {
    setConfig((c) => {
      const next = { ...(c.durations || {}) };
      delete next[id];
      // Also remove from per-service overrides
      const cleanedServices = {};
      Object.entries(c.services || {}).forEach(([sid, sv]) => {
        const sd = { ...(sv.durations || {}) };
        delete sd[id];
        cleanedServices[sid] = { ...sv, durations: sd };
      });
      return { ...c, durations: next, services: cleanedServices };
    });
  };

  const updateServiceDuration = (serviceId, durationId, value) => {
    setConfig((c) => ({
      ...c,
      services: {
        ...c.services,
        [serviceId]: {
          ...c.services[serviceId],
          durations: {
            ...(c.services[serviceId]?.durations || {}),
            [durationId]: value,
          },
        },
      },
    }));
  };

  const formatDurationFr = (m) => {
    const mins = parseInt(m, 10);
    if (!Number.isFinite(mins) || mins <= 0) return `${m}`;
    if (mins < 60) return `${mins} minutes`;
    const h = Math.floor(mins / 60);
    const r = mins % 60;
    if (r === 0) return h === 1 ? "1 heure" : `${h} heures`;
    return `${h} h ${r}`;
  };

  const updateContact = (field, value) => {
    setConfig((c) => ({ ...c, [field]: value }));
  };

  const toggleBlocked = (date) => {
    const iso = format(date, "yyyy-MM-dd");
    setConfig((c) => {
      const blocked = c.blockedDates || [];
      const exists = blocked.includes(iso);
      return {
        ...c,
        blockedDates: exists ? blocked.filter((d) => d !== iso) : [...blocked, iso],
      };
    });
  };

  const handleSave = async () => {
    setSavingMsg("");
    try {
      await api.putConfig(config);
      setSaved(true);
      window.dispatchEvent(new CustomEvent("anypaku-config-changed"));
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSavingMsg(formatApiError(err));
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Réinitialiser tous les prix et dates aux valeurs par défaut ?"))
      return;
    setConfig(DEFAULT_CONFIG);
    try {
      await api.putConfig(DEFAULT_CONFIG);
      window.dispatchEvent(new CustomEvent("anypaku-config-changed"));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSavingMsg(formatApiError(err));
    }
  };

  const blockedDateObjs = (config.blockedDates || []).map((d) => new Date(d + "T00:00:00"));

  /* ---------- LOGIN VIEW ---------- */
  if (checkingAuth) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--cream)", color: "var(--mute)" }}
      >
        Chargement…
      </div>
    );
  }
  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-5"
        style={{ background: "var(--cream)" }}
      >
        <form
          onSubmit={submitPw}
          className="w-full max-w-md p-8 rounded-3xl shadow-2xl"
          style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
          data-testid="admin-login-form"
        >
          <div
            className="display"
            style={{ fontSize: 38, color: "var(--navy)", lineHeight: 0.9 }}
          >
            ANYP<span style={{ color: "var(--orange)" }}>A</span>KU
          </div>
          <div
            className="mt-1 text-xs"
            style={{
              color: "var(--mute)",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontFamily: "var(--font-mono)",
            }}
          >
            Admin Panel — v2
          </div>

          <h1 className="mt-6 text-2xl font-bold" style={{ color: "var(--navy)" }}>
            Connexion admin
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--mute)" }}>
            Entre ton email et mot de passe.
          </p>

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full mt-5 px-4 py-3 rounded-xl border outline-none text-[15px]"
            style={{ borderColor: "#DCE3ED", color: "var(--navy)" }}
            autoFocus
            data-testid="admin-email-input"
            required
          />
          <input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Mot de passe"
            className="w-full mt-3 px-4 py-3 rounded-xl border outline-none text-[15px]"
            style={{ borderColor: "#DCE3ED", color: "var(--navy)" }}
            data-testid="admin-password-input"
            required
          />
          {pwError && (
            <div
              className="mt-3 text-sm"
              style={{ color: "#B3263F" }}
              data-testid="admin-password-error"
            >
              {pwError}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full mt-4 justify-center"
            data-testid="admin-login-submit"
          >
            {submitting ? "Connexion…" : "Se connecter →"}
          </button>

          <a
            href="/"
            className="block mt-6 text-center text-sm hover:underline"
            style={{ color: "var(--mute)" }}
          >
            ← Retour au site
          </a>
        </form>
      </div>
    );
  }

  /* ---------- ADMIN DASHBOARD ---------- */
  return (
    <div className="min-h-screen pb-20" style={{ background: "var(--cream)" }} data-testid="admin-dashboard">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 px-5 md:px-10 py-4 flex items-center justify-between"
        style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="display"
            style={{ fontSize: 22, color: "var(--navy)" }}
          >
            ANYP<span style={{ color: "var(--orange)" }}>A</span>KU
          </div>
          <span
            className="hidden sm:inline-flex"
            style={{
              background: "var(--orange-soft)",
              color: "var(--orange-2)",
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 10,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
            }}
          >
            Admin v2
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" className="btn-ghost" style={{ padding: "8px 16px", fontSize: 13 }} data-testid="admin-back-home">
            ← Site
          </a>
          <button onClick={logout} className="btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} data-testid="admin-logout">
            Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-10 py-10 space-y-8">
        <div>
          <h1
            className="display"
            style={{ fontSize: "clamp(36px, 5vw, 64px)", color: "var(--navy)" }}
          >
            Tableau de bord
          </h1>
          <p className="mt-2 text-base" style={{ color: "var(--mute)" }}>
            Niveau 2 — base de données sécurisée
          </p>
        </div>

        <Notice kind="success">
          ✅ <strong>Niveau 2 actif :</strong> les changements ici sont enregistrés en base de données
          et <strong>immédiatement visibles par tous les visiteurs</strong> du site, sur tous les appareils.
        </Notice>

        {/* PRICES */}
        <section
          className="rounded-3xl p-6 md:p-8"
          style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
          data-testid="admin-prices-section"
        >
          <div className="flex items-baseline justify-between mb-4">
            <h2
              className="display"
              style={{ fontSize: 26, color: "var(--navy)" }}
            >
              💰 Prix des services
            </h2>
          </div>

          {[
            { id: "noLabour", title: "Vain kuljetus", sub: "Transport seul" },
            { id: "driverHelp", title: "Kuljettaja avustaa kantamista", sub: "Chauffeur aide à la manutention" },
            { id: "twoMen", title: "Kuljettaja avustaa kantamista + apulainen", sub: "Chauffeur + 2ème personne" },
          ].map((s) => (
            <div
              key={s.id}
              className="rounded-2xl p-4 mt-3"
              style={{ background: "var(--cream-2)" }}
              data-testid={`admin-service-${s.id}`}
            >
              <div className="font-bold" style={{ color: "var(--navy)" }}>{s.title}</div>
              <div className="text-xs mb-3" style={{ color: "var(--mute)" }}>{s.sub}</div>
              <Row label="Prix par heure (€/h)" sub="Le prix affiché sur la carte">
                <PriceInput
                  value={config.services[s.id].base}
                  onChange={(v) => updateService(s.id, "base", v)}
                />
              </Row>
              <Row label="Km inclus" sub="Km couverts par le prix de base (gratuit jusqu'à cette distance)">
                <PriceInput
                  value={config.services[s.id].includedKm ?? 0}
                  onChange={(v) => updateService(s.id, "includedKm", v)}
                  suffix="km"
                  width={110}
                />
              </Row>
              <Row label="Coût au kilomètre" sub="Appliqué au-delà des km inclus">
                <PriceInput
                  value={config.services[s.id].perKm}
                  onChange={(v) => updateService(s.id, "perKm", v)}
                  suffix="€/km"
                  width={130}
                />
              </Row>
            </div>
          ))}
        </section>

        {/* DURATIONS */}
        <section
          className="rounded-3xl p-6 md:p-8"
          style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
          data-testid="admin-durations-section"
        >
          <h2
            className="display"
            style={{ fontSize: 26, color: "var(--navy)" }}
          >
            ⏱ Suppléments durée
          </h2>
          <p className="mt-1 mb-3 text-sm" style={{ color: "var(--mute)" }}>
            Définis un prix <strong>par service</strong> pour chaque durée. Ajoute autant de durées que tu veux (ex. 6 h, 9 h).
          </p>

          {(() => {
            const sortedDurations = Object.keys(config.durations || {})
              .map((k) => ({ id: String(k), minutes: parseInt(k, 10) }))
              .filter((d) => Number.isFinite(d.minutes) && d.minutes > 0)
              .sort((a, b) => a.minutes - b.minutes);
            const SERVICE_COLS = [
              { id: "noLabour", title: "Transport seul" },
              { id: "driverHelp", title: "Chauffeur aide" },
              { id: "twoMen", title: "Chauffeur + 2ème" },
            ];
            const getCellValue = (sId, dId) => {
              const v = config.services?.[sId]?.durations?.[dId];
              if (v !== undefined && v !== null) return v;
              // Fallback to global durations price (legacy)
              return config.durations?.[dId] ?? 0;
            };
            return (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: "0 6px" }}>
                  <thead>
                    <tr>
                      <th
                        className="text-left text-xs font-bold pb-2 pl-1"
                        style={{
                          color: "var(--mute)",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          fontFamily: "var(--font-mono)",
                          minWidth: 90,
                        }}
                      >
                        Durée
                      </th>
                      {SERVICE_COLS.map((s) => (
                        <th
                          key={s.id}
                          className="text-xs font-bold pb-2 px-1 text-center"
                          style={{
                            color: "var(--navy)",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            fontFamily: "var(--font-mono)",
                          }}
                        >
                          {s.title}
                        </th>
                      ))}
                      <th style={{ width: 90 }} aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDurations.map((d) => (
                      <tr key={d.id}>
                        <td
                          className="font-bold pl-1 pr-2"
                          style={{ color: "var(--navy)", fontSize: 14 }}
                        >
                          {formatDurationFr(d.minutes)}
                        </td>
                        {SERVICE_COLS.map((s) => (
                          <td key={s.id} className="px-1">
                            <div
                              className="flex items-center rounded-xl border overflow-hidden mx-auto"
                              style={{ borderColor: "#DCE3ED", background: "#FFFFFF", width: 110 }}
                            >
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={getCellValue(s.id, d.id)}
                                onChange={(e) =>
                                  updateServiceDuration(s.id, d.id, Number(e.target.value))
                                }
                                className="w-full px-2 py-2 bg-transparent outline-none text-right font-bold"
                                style={{ color: "var(--navy)", fontSize: 14 }}
                                data-testid={`dur-${s.id}-${d.id}`}
                              />
                              <span
                                style={{
                                  background: "var(--cream)",
                                  padding: "8px 8px",
                                  color: "var(--mute)",
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                €
                              </span>
                            </div>
                          </td>
                        ))}
                        <td className="pl-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeDuration(d.id)}
                            className="px-2 py-1.5 rounded-lg text-xs font-bold"
                            style={{ background: "#FFE9EA", color: "#B3263F" }}
                            data-testid={`duration-remove-${d.id}`}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          <AddDurationForm onAdd={addDuration} existing={config.durations || {}} />
        </section>

        {/* BLOCKED DATES */}
        <section
          className="rounded-3xl p-6 md:p-8"
          style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
          data-testid="admin-blocked-section"
        >
          <h2
            className="display"
            style={{ fontSize: 26, color: "var(--navy)" }}
          >
            📅 Dates indisponibles
          </h2>
          <p className="mt-1 mb-4 text-sm" style={{ color: "var(--mute)" }}>
            Clique sur une date pour la bloquer (vacances, jour complet, etc.). Les dates bloquées
            apparaîtront grisées dans le calendrier du client.
          </p>
          <div
            className="rounded-2xl p-3 inline-block"
            style={{ background: "var(--cream-2)" }}
          >
            <DayPicker
              mode="multiple"
              selected={blockedDateObjs}
              onSelect={() => {}}
              onDayClick={(d) => toggleBlocked(d)}
              weekStartsOn={1}
              showOutsideDays
              disabled={{ before: new Date() }}
              modifiersClassNames={{
                selected: "anypaku-day-blocked",
                today: "anypaku-day-today",
              }}
            />
          </div>
          {blockedDateObjs.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold mb-2" style={{ color: "var(--mute)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {blockedDateObjs.length} date(s) bloquée(s)
              </div>
              <div className="flex flex-wrap gap-2">
                {blockedDateObjs.sort((a, b) => a - b).map((d) => (
                  <span
                    key={d.toISOString()}
                    className="px-3 py-1 rounded-full text-xs flex items-center gap-2"
                    style={{ background: "#FFE9EA", color: "#B3263F", border: "1px solid #F5C2C8" }}
                  >
                    {format(d, "dd MMM yyyy")}
                    <button
                      type="button"
                      onClick={() => toggleBlocked(d)}
                      style={{ fontWeight: 800 }}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* CONTACT */}
        <section
          className="rounded-3xl p-6 md:p-8"
          style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
          data-testid="admin-contact-section"
        >
          <h2 className="display mb-4" style={{ fontSize: 26, color: "var(--navy)" }}>
            📞 Contact
          </h2>
          <Row label="Téléphone (affichage)" sub="Visible sur le site">
            <input
              type="text"
              value={config.phoneDisplay}
              onChange={(e) => updateContact("phoneDisplay", e.target.value)}
              className="px-3 py-2 rounded-xl border outline-none text-[14px]"
              style={{ borderColor: "#DCE3ED", color: "var(--navy)", minWidth: 200 }}
            />
          </Row>
          <Row label="Téléphone (lien tel:)" sub="Format sans espaces, avec +">
            <input
              type="text"
              value={config.phoneRaw}
              onChange={(e) => updateContact("phoneRaw", e.target.value)}
              className="px-3 py-2 rounded-xl border outline-none text-[14px] font-mono"
              style={{ borderColor: "#DCE3ED", color: "var(--navy)", minWidth: 200 }}
            />
          </Row>
          <Row label="Lien WhatsApp" sub="https://wa.me/...">
            <input
              type="text"
              value={config.whatsappLink}
              onChange={(e) => updateContact("whatsappLink", e.target.value)}
              className="px-3 py-2 rounded-xl border outline-none text-[14px] font-mono"
              style={{ borderColor: "#DCE3ED", color: "var(--navy)", minWidth: 280 }}
            />
          </Row>
        </section>

        {/* SITE TEXTS (CMS-lite) */}
        <AdminTextsSection />

        {/* DYNAMIC BUILDER (FAQ / Why / Image / Colors) */}
        <AdminBuilder config={config} onChange={setConfig} />

        {/* CHANGE PASSWORD */}
        <AdminPasswordSection />

        {/* SAVE BAR */}
        <div
          className="sticky bottom-0 -mx-5 md:-mx-10 px-5 md:px-10 py-4 flex flex-col sm:flex-row items-center gap-3 justify-between"
          style={{
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(14px)",
            borderTop: "1px solid var(--line)",
          }}
        >
          <div className="text-sm" style={{ color: "var(--mute)" }}>
            {savingMsg ? (
              <span style={{ color: "#B3263F" }}>⚠️ {savingMsg}</span>
            ) : saved ? (
              <span style={{ color: "#1A6F3A" }}>✓ Enregistré sur le serveur — visible pour tous</span>
            ) : configLoading ? (
              "Chargement de la configuration…"
            ) : (
              "Pense à enregistrer après tes modifications"
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button onClick={handleReset} className="btn-ghost flex-1 sm:flex-none" data-testid="admin-reset">
              Réinitialiser
            </button>
            <button onClick={handleSave} className="btn-primary flex-1 sm:flex-none" data-testid="admin-save">
              💾 Enregistrer
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
